/**
 * useAuthState.js — État et logique d'authentification
 * Extrait de useAppState pour isolation et testabilité
 *
 * Responsabilités :
 *   - Restauration de session au démarrage (localStorage + cookie httpOnly)
 *   - Login / logout / 2FA
 *   - Détection token de partage dans l'URL
 */
import { useState, useEffect, useCallback } from 'react';
import { PAGES } from '../constants/pages';
import { clearAllUserDrafts } from '../vetpro/draftStorage';

// Clé localStorage de l'état nav Mode Pro Vét (= STORAGE_KEY de useVetProState).
// Littéral inline (V5a) : l'auth ne dépend plus de useVetProState — son retrait
// (V5b) devient une suppression INTERNE au module vetpro. Cleanup défensif d'un
// éventuel état persisté par un ancien client ; doit rester strictement iso.
const VET_PRO_STORAGE_KEY = 'ekynair:vetProState';

export function useAuthState({ onAuthenticated, onLogout }) {
  const [authPage,             setAuthPage]             = useState('loading');
  const [pending2FA,           setPending2FA]           = useState(null);
  const [currentUser,          setCurrentUser]          = useState(null);
  const [verificationMessage,  setVerificationMessage]  = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Messages de vérification email
    if (params.get('verification') === 'success') {
      setVerificationMessage({ type: 'success', text: 'Email vérifié ! Vous pouvez vous connecter.' });
      window.history.replaceState({}, '', '/');
    } else if (params.get('verification') === 'expired') {
      setVerificationMessage({ type: 'error', text: 'Lien expiré. Reconnectez-vous pour en recevoir un nouveau.' });
      window.history.replaceState({}, '', '/');
    }

    // Token de partage en attente
    const shareToken = params.get('share');
    if (shareToken) {
      localStorage.setItem('ekynair_pending_share', shareToken);
      window.history.replaceState({}, '', '/');
    }

    // Page reset password
    if (window.location.pathname === '/reset-password' || window.location.search.includes('token=')) {
      setAuthPage('reset');
      return;
    }

    // Routes publiques (légales + support) — pas de tentative de refresh.
    // Sans cette whitelist, le 401 sur /api/auth/refresh force authPage='login'
    // et la page légale n'est jamais rendue (bug Apple 5.1.1 / Google Play).
    // App.jsx court-circuite le gate via son bloc publicLegal avant d'utiliser authPage.
    const PUBLIC_LEGAL_PATHS = [
      '/legal/mentions', '/legal/cgu', '/legal/privacy',
      '/legal/cookies', '/legal/moderation', '/support',
    ];
    if (PUBLIC_LEGAL_PATHS.includes(window.location.pathname)) {
      setAuthPage('public');
      return;
    }

    tryRestoreSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tryRestoreSession = async () => {
    // SEC-4 : token non lu depuis localStorage — session via cookie httpOnly
    const token = null; // Toujours null pour forcer le refresh via cookie
    const userStr = localStorage.getItem('ekynair_user');

    // Cas 1 : token localStorage présent et non expiré
    if (token && userStr) {
      try {
        const user    = JSON.parse(userStr);
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (!payload.exp || payload.exp * 1000 > Date.now()) {
          setCurrentUser(user);
          setAuthPage('app');
          onAuthenticated?.();
          return;
        }
        // SEC-4 : pas de token localStorage à nettoyer
      } catch { /* JWT invalide → tenter refresh */ }
    }

    // Cas 2 : refresh via cookie httpOnly (comportement normal en web)
    try {
      const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data?.success && data?.user) {
          localStorage.setItem('ekynair_user', JSON.stringify(data.user));
          if (data.token) // SEC-4 : token JWT non stocké en localStorage — cookie httpOnly utilisé pour le web
          setCurrentUser(data.user);
          setAuthPage('app');
          onAuthenticated?.();
          return;
        }
      }
    } catch { /* réseau indisponible */ }

    setAuthPage('login');
  };

  // Mémoïsation : ces handlers sont consommés en deps de useCallback dans
  // useAppState (doLogout → loadHorses/loadSessions → useEffect). Sans
  // useCallback ici, chaque render produit de nouvelles refs en cascade et
  // déclenche une boucle infinie de fetch (BUG-CRIT 2026-04-18).
  const handle2FASuccess = useCallback((data) => {
    // SEC-4 : token JWT non stocké en localStorage — cookie httpOnly utilisé pour le web
    if (data.token && data.user) {
      setCurrentUser(data.user);
      localStorage.setItem('ekynair_user', JSON.stringify(data.user));
    }
    setPending2FA(null);
    setAuthPage('app');
  }, []);

  const handleLogin = useCallback((user, { setCurrentPage, loadSessions } = {}) => {
    setCurrentUser(user);
    setAuthPage('app');

    // Reset dur de la nav profonde Mode Pro Vét au login : on vide l'état
    // persisté pour que VetProRoot remonte sur l'atterrissage 'sessions'
    // (DEFAULT_STATE useVetProState). Le refresh, lui, passe par
    // tryRestoreSession (cookie) et NE vide PAS → les pages profondes y sont
    // respectées. Sprint 2026-05-29 (atterrissage Sessions, 2 modes).
    localStorage.removeItem(VET_PRO_STORAGE_KEY);

    const pendingShare = localStorage.getItem('ekynair_pending_share');
    if (pendingShare) {
      localStorage.removeItem('ekynair_pending_share');
      // SEC-4 : utiliser credentials:include (cookie httpOnly) plutôt que Bearer localStorage
      fetch(`/api/shares/${pendingShare}/accept`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }).then(r => r.json()).then(d => { if (d.success) setCurrentPage?.(PAGES.HORSES); }).catch(() => {});
      setCurrentPage?.(PAGES.HORSES);
    } else {
      setCurrentPage?.(PAGES.LIST);
    }
  }, []);

  const handleLogout = useCallback(async ({ setCurrentPage, setHorses, setSessions } = {}) => {
    try { await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }); } catch {}
    // RGPD (poste partagé) : effacer les brouillons de cas véto de l'utilisateur
    // courant avant de couper la session. userId lu depuis `ekynair_user`
    // localStorage, encore présent ici (supprimé juste après).
    try {
      const stored = JSON.parse(localStorage.getItem('ekynair_user') || 'null');
      clearAllUserDrafts(stored?._id || stored?.id);
    } catch { /* user illisible — purge ignorée */ }
    // SEC-4 : pas de token localStorage à nettoyer
    localStorage.removeItem('ekynair_user');
    localStorage.removeItem('ekynair-current-page');
    localStorage.removeItem(VET_PRO_STORAGE_KEY);
    setCurrentUser(null);
    setAuthPage('login');
    setHorses?.([]);
    setSessions?.([]);
    onLogout?.();
  }, [onLogout]);

  return {
    authPage, setAuthPage,
    currentUser, setCurrentUser,
    verificationMessage,
    pending2FA, setPending2FA,
    handle2FASuccess, handleLogin, handleLogout,
  };
}
